import React, { useState } from 'react';
import { stackAPI, twistedStackAPI } from '../services/api';
import { getDiceBearUserAvatar } from '../services/dicebear';
import EditableTextArea from './EditableTextArea';
import { useConfirmationDialog, acknowledgeAction } from '../utils/confirmationDialog';
import { apiErrorMessage } from '../utils/apiError';
import { BUTTON_COLORS } from '../utils/buttonColors';
import CharacterCounter from '../utils/characterCounter';

const Comments = ({ 
  comments = [], 
  currentUser, 
  stackId, // For regular stacks, this is the stack ID. For twisted stacks, this is the twisted stack ID.
  isTwisted = false,
  onCommentUpdate,
  canSandbox = false // Whether user can sandbox comments (admin or group owner)
}) => {
  const [newComment, setNewComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [editingCommentId, setEditingCommentId] = useState(null);
  const [deletingCommentId, setDeletingCommentId] = useState(null);
  const [sandboxingCommentId, setSandboxingCommentId] = useState(null);

  // Count newline characters in the comment
  const newlineCount = (newComment.match(/\n/g) || []).length;
  const hasTooManyNewlines = newlineCount > 3;

  // Confirmation dialog
  const { showConfirmation, ConfirmationDialog } = useConfirmationDialog();

  const api = isTwisted ? twistedStackAPI : stackAPI;

  const handleSubmitComment = async () => {
    if (!newComment.trim() || submitting || hasTooManyNewlines) return;
    
    try {
      setSubmitting(true);
      // Strip newlines beyond the 3rd one
      let commentText = newComment.trim();
      const newlines = commentText.match(/\n/g) || [];
      if (newlines.length > 3) {
        // Find the 3rd newline and truncate everything after it
        let newlineCount = 0;
        let truncateIndex = -1;
        for (let i = 0; i < commentText.length; i++) {
          if (commentText[i] === '\n') {
            newlineCount++;
            if (newlineCount === 3) {
              truncateIndex = i;
              break;
            }
          }
        }
        if (truncateIndex !== -1) {
          commentText = commentText.substring(0, truncateIndex);
        }
      }
      
      await api.createComment(stackId, commentText);
      setNewComment('');
      onCommentUpdate(); // Refresh comments
    } catch (err) {
      console.error('Failed to create comment:', err);
      await acknowledgeAction(
        'Error',
        apiErrorMessage(err, 'Failed to create comment. Please try again.')
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditComment = async (commentId, description) => {
    try {
      await api.updateComment(stackId, commentId, description);
      setEditingCommentId(null);
      onCommentUpdate(); // Refresh comments
    } catch (err) {
      console.error('Failed to update comment:', err);
      throw err; // Let EditableTextArea handle the error display
    }
  };

  const handleDeleteComment = async (commentId) => {
    showConfirmation({
      title: 'Delete Comment',
      message: 'Are you sure you want to delete this comment? This action cannot be undone.',
      confirmText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonColor: BUTTON_COLORS.DELETE, // Red for delete action
      cancelButtonColor: BUTTON_COLORS.CANCEL, // Gray for cancel
      onConfirm: async () => {
        try {
          setDeletingCommentId(commentId);
          await api.deleteComment(stackId, commentId);
          onCommentUpdate(); // Refresh comments
        } catch (err) {
          console.error('Failed to delete comment:', err);
          await acknowledgeAction(
            'Error',
            'Failed to delete comment. Please try again.'
          );
        } finally {
          setDeletingCommentId(null);
        }
      },
      onCancel: () => {
        // Do nothing on cancel
      },
    });
  };

  const handleSandboxComment = async (commentId, sandbox) => {
    const isSandboxing = sandbox === true;
    showConfirmation({
      title: isSandboxing ? 'Sandbox Comment' : 'Unsandbox Comment',
      message: isSandboxing 
        ? 'Are you sure you want to sandbox this comment? It will be hidden from other users.'
        : 'Are you sure you want to unsandbox this comment? It will be visible to other users again.',
      confirmText: isSandboxing ? 'Sandbox' : 'Unsandbox',
      cancelText: 'Cancel',
      confirmButtonColor: isSandboxing ? BUTTON_COLORS.SANDBOX : BUTTON_COLORS.UNSANDBOX,
      cancelButtonColor: BUTTON_COLORS.CANCEL,
      onConfirm: async () => {
        try {
          setSandboxingCommentId(commentId);
          await api.sandboxComment(stackId, commentId, sandbox);
          onCommentUpdate(); // Refresh comments
        } catch (err) {
          console.error(`Failed to ${isSandboxing ? 'sandbox' : 'unsandbox'} comment:`, err);
          await acknowledgeAction(
            'Error',
            `Failed to ${isSandboxing ? 'sandbox' : 'unsandbox'} comment. Please try again.`
          );
        } finally {
          setSandboxingCommentId(null);
        }
      },
      onCancel: () => {
        // Do nothing on cancel
      },
    });
  };

  const formatTimestamp = (timestamp) => {
    try {
      return new Date(timestamp).toLocaleString();
    } catch {
      return timestamp;
    }
  };

  const renderComment = (comment) => {
    const isOwnComment = currentUser && comment.user_id === currentUser.id;
    const isEditing = editingCommentId === comment.id;
    const isDeleting = deletingCommentId === comment.id;
    const isSandboxing = sandboxingCommentId === comment.id;

    return (
      <div key={comment.id} style={{ 
        border: '1px solid var(--color-border)', 
        borderRadius: '8px', 
        padding: '12px', 
        marginBottom: '12px',
        backgroundColor: 'var(--color-surface)'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <img 
              src={getDiceBearUserAvatar(comment.username, comment.avatar_id || 1)} 
              alt={`${comment.username} avatar`} 
              style={{ width: '28px', height: '28px', borderRadius: '50%' }} 
            />
            <button
              type="button"
              onClick={() => {
                try { 
                  window.dispatchEvent(new CustomEvent('openUser', { detail: { userId: comment.user_id } })); 
                } catch (_) {}
              }}
              style={{ 
                background: 'transparent', 
                border: 'none', 
                padding: 0, 
                cursor: 'pointer',
                color: 'var(--color-primary)',
                textDecoration: 'underline',
                fontWeight: '600'
              }}
            >
              {comment.username}
            </button>
          </div>
          <div style={{ fontSize: '12px', color: 'var(--color-text-muted)' }}>
            {formatTimestamp(comment.created_at)}
          </div>
        </div>

        {isEditing ? (
          <EditableTextArea
            value={comment.description}
            onSave={(text) => handleEditComment(comment.id, text)}
            onRevert={() => setEditingCommentId(null)}
            placeholder="Enter comment..."
            maxLength={200}
            label=""
            loading={false}
          />
        ) : (
          <div style={{ marginBottom: '8px', color: 'var(--color-text)' }}>
            {comment.description}
          </div>
        )}

        {!isEditing && (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            {isOwnComment && (
              <>
                <button
                  type="button"
                  onClick={() => setEditingCommentId(comment.id)}
                  className="btn"
                  style={{ 
                    backgroundColor: BUTTON_COLORS.EDIT, 
                    color: 'white', 
                    fontSize: '12px', 
                    padding: '4px 8px',
                    border: 'none'
                  }}
                >
                  Edit
                </button>
                <button
                  type="button"
                  onClick={() => handleDeleteComment(comment.id)}
                  className="btn"
                  disabled={isDeleting}
                  style={{ 
                    backgroundColor: BUTTON_COLORS.DELETE, 
                    color: 'white', 
                    fontSize: '12px', 
                    padding: '4px 8px',
                    border: 'none',
                    opacity: isDeleting ? 0.6 : 1
                  }}
                >
                  {isDeleting ? 'Deleting...' : 'Delete'}
                </button>
              </>
            )}
            {canSandbox && (
              <button
                type="button"
                onClick={() => handleSandboxComment(comment.id, !comment.is_sandboxed)}
                className="btn"
                disabled={isSandboxing}
                style={{ 
                  backgroundColor: comment.is_sandboxed ? BUTTON_COLORS.UNSANDBOX : BUTTON_COLORS.SANDBOX, 
                  color: 'white', 
                  fontSize: '12px', 
                  padding: '4px 8px',
                  border: 'none',
                  opacity: isSandboxing ? 0.6 : 1
                }}
              >
                {isSandboxing ? (comment.is_sandboxed ? 'Unsandboxing...' : 'Sandboxing...') : (comment.is_sandboxed ? 'Unsandbox' : 'Sandbox')}
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div style={{ marginTop: '20px' }}>
      <h3>Comments</h3>
      
      {comments.length === 0 ? (
        <div style={{ 
          color: 'var(--color-text-muted)', 
          fontStyle: 'italic', 
          marginBottom: '16px' 
        }}>
          No comments
        </div>
      ) : (
        <div style={{ marginBottom: '16px' }}>
          {comments.map(renderComment)}
        </div>
      )}

      <div style={{ 
        border: '1px solid var(--color-border)', 
        borderRadius: '8px', 
        padding: '12px',
        backgroundColor: 'var(--color-surface)'
      }}>
        <textarea
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          placeholder="Write a comment..."
          maxLength={200}
          style={{
            width: '100%',
            minHeight: '60px',
            padding: '8px',
            border: '1px solid var(--color-border)',
            borderRadius: '4px',
            fontSize: '14px',
            resize: 'vertical',
            background: 'var(--color-surface)',
            color: 'var(--color-text)',
            marginBottom: '8px'
          }}
        />
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center' 
        }}>
          <div style={{ 
            fontSize: '12px', 
            color: 'var(--color-text-muted)' 
          }}>
            <CharacterCounter current={newComment.length} max={200} style={{ textAlign: 'left', marginTop: 0 }} />
            {hasTooManyNewlines && (
              <span style={{ 
                color: '#dc3545', 
                marginLeft: '8px',
                fontWeight: 'bold'
              }}>
                Too many new line characters.
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={handleSubmitComment}
            className="btn"
            disabled={!newComment.trim() || submitting || hasTooManyNewlines}
            style={{ 
              backgroundColor: BUTTON_COLORS.SEND, 
              color: 'white', 
              border: 'none',
              opacity: (!newComment.trim() || submitting || hasTooManyNewlines) ? 0.6 : 1
            }}
          >
            {submitting ? 'Sending...' : 'Send Comment'}
          </button>
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      <ConfirmationDialog />
    </div>
  );
};

export default Comments;
